import * as React from 'react';
import { jobsApi } from '../services/jobsApi';
import type { JobLogEntry } from '../types/jobs';

const POLL_INTERVAL_MS = 4_000;

export function useJobLogs(projectUuid: string, runUuid: string | null, isActive: boolean) {
  const [logs, setLogs] = React.useState<JobLogEntry[]>([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  // cursor = ID of the last fetched log entry (cursor-based pagination)
  const lastIdRef = React.useRef<number | null>(null);

  const fetchPage = React.useCallback(
    async (cursor?: number) => {
      if (!runUuid) return;
      if (!cursor) setLoading(true);
      setError(null);
      try {
        const result = await jobsApi.getJobLogs(projectUuid, runUuid, cursor);
        setLogs((prev) => (cursor ? [...prev, ...result.logs] : result.logs));
        setHasMore(result.hasMore);
        if (result.logs.length > 0) {
          lastIdRef.current = result.logs[result.logs.length - 1]!.id;
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        if (!cursor) setLoading(false);
      }
    },
    [projectUuid, runUuid]
  );

  React.useEffect(() => {
    if (!runUuid) return;
    void fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runUuid]);

  // Poll for new logs while the run is active.
  // When the run transitions from active → done, do one final fetch to capture trailing entries.
  const wasActiveRef = React.useRef(false);
  React.useEffect(() => {
    if (isActive && runUuid) {
      intervalRef.current = setInterval(() => void fetchPage(), POLL_INTERVAL_MS);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Run just finished - fetch one more time to pick up completion log entries.
      if (wasActiveRef.current && runUuid) {
        void fetchPage();
      }
    }
    wasActiveRef.current = isActive;
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, runUuid, fetchPage]);

  const loadMore = React.useCallback(() => {
    if (hasMore && lastIdRef.current !== null) {
      void fetchPage(lastIdRef.current);
    }
  }, [hasMore, fetchPage]);

  return { logs, hasMore, loading, error, loadMore };
}
